import * as d3 from 'd3';
import { sankey as d3Sankey, sankeyJustify } from 'd3-sankey';
import { linkHorizontal } from 'd3-shape';
import React, { useCallback, useEffect } from 'react';

import { SankeyLink, SankeyLinkWithCoordinates, SankeyNode } from './types';

type UseSankeyDiagramParams = {
	height?: number,
	fontSize?: number,
	sankeyNodes: SankeyNode[],
	sankeyLinks: SankeyLink[],
	htmlDivElementRef: React.RefObject<HTMLDivElement>
}

export const useSankeyDiagram = ({
  height = 600,
  fontSize = 12,
  sankeyNodes,
  sankeyLinks,
  htmlDivElementRef
}: UseSankeyDiagramParams) => {
  const drawSankey = useCallback(() => {
    const container = htmlDivElementRef.current;
    if (!container || sankeyNodes.length <= 0 || sankeyLinks.length <= 0) { return; }
    const nodeKey = (node: SankeyNode) => node.id ?? node.name;
    if (sankeyLinks.length > 0 && !sankeyLinks.some((link) => sankeyNodes.some((node) => nodeKey(node) === link.source))) return;
    if (sankeyLinks.length > 0 && !sankeyLinks.some((link) => sankeyNodes.some((node) => nodeKey(node) === link.target))) return;

    d3.select(container).html(null);

    try {
      const measuredWidth = container.getBoundingClientRect().width;
      const width = measuredWidth > 0 ? measuredWidth : document.body.getBoundingClientRect().width;
      const format = d3.format(',.0f');

      const sankey = d3Sankey()
        .nodeAlign(sankeyJustify)
        .nodeId((d) => d.id ?? d.name)
        .extent([[1, 5], [width - 1, height - 5]])
        .nodePadding(10)
        .nodeWidth(15);

      const { nodes, links } = sankey({
        nodes: sankeyNodes.map((node) => ({ ...node })),
        links: sankeyLinks.map((link) => ({ ...link }))
      });

      const path = linkHorizontal<SankeyLinkWithCoordinates, [number, number]>()
        .source((d) => [d.source.x1 || 0, d.y0])
        .target((d) => [d.target.x0 || 0, d.y1]);

      const svg = d3.select(container)
        .append('svg')
        .attr('viewBox', [0, 0, width, height])
        .attr('width', width)
        .attr('height', height)
        .attr('style', `max-width: 100%; height: auto; font: ${fontSize}px sans-serif;`);

      const color = d3.scaleOrdinal(d3.schemeCategory10);
      const rect = svg.append('g')
        .attr('stroke', '#000')
        .selectAll()
        .data(nodes)
        .join('rect')
        .attr('x', (d) => d.x0)
        .attr('y', (d) => d.y0)
        .attr('height', (d) => d.y1 - d.y0)
        .attr('width', (d) => d.x1 - d.x0)
        .attr('fill', (d) => color(d.category));

      rect.append('title')
        .text((d) => `${d.name}\n${format(d.value)} USD`);

      const link = svg.append('g')
        .attr('fill', 'none')
        .attr('stroke-opacity', 0.5)
        .selectAll()
        .data(links)
        .join('g')
        .style('mix-blend-mode', 'multiply');

      link.append('path')
        .attr('d', (d) => path(d))
        .attr('stroke', (d) => color(d.target.category))
        .attr('stroke-width', (d) => Math.max(1, d.width));

      link.append('title')
        .text((d) => `${d.source.name} → ${d.target.name}\n${format(d.value)} USD`);

      svg.append('g')
        .selectAll()
        .data(nodes)
        .join('text')
        .attr('x', (d) => (d.x0 < width / 2 ? d.x1 + 6 : d.x0 - 6))
        .attr('y', (d) => (d.y1 + d.y0) / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', (d) => (d.x0 < width / 2 ? 'start' : 'end'))
        .text((d) => d.name);
    } catch (error) {
      d3.select(container).html(null);
    }
  }, [height, fontSize, sankeyNodes, sankeyLinks, htmlDivElementRef]);

  useEffect(() => {
    if (!htmlDivElementRef.current) {
      return () => { };
    }
    drawSankey();
    window.addEventListener('resize', drawSankey);

    return () => {
      window.removeEventListener('resize', drawSankey);
    };
  }, [drawSankey, htmlDivElementRef]);

  return { drawSankey };
};
